/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  FilterNames,
  FilterRestService,
  isJobElementTotal,
  JobElementTotal,
  JobElementType,
} from '@gh/builder-shared';
import { BuilderPermission, DatesFilter, JobStatus, SystemRoleBit } from '@gh/builder-shared-data';
import {
  AppRouter,
  DateTimeTypes,
  EnumDictionary,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  NumberTypes,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService, TextMessageDialogConfig, WaitingContext, WaitingMode } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumnLeft,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { JobListBusinessService } from '../../resources/job-list-business.service';
import { JobListFilter, JobListItem } from '../../resources/job.model';
import { JobNavigationService } from '../../resources/job-navigation.service';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  conditionalRowPostCreateDecorator,
  GridSettingsService,
  TABLE_HAS_PRESETS,
  textMessageDialogRoute,
  textOverlayRowPostCreate,
} from '@gh/common-internal-shared';
import { TranslationService } from '@gh/core-mls';
import { createLink, toString } from '@gh/core-util';
import { padStart } from 'lodash';

type predefinedFilter = {
  id: string;
  name: string;
};
@Component({
  selector: 'gh-job-list',
  styles: [require('./job-list.component.scss')],
  template: require('./job-list.component.html'),
})
export class JobListComponent implements OnInit, OnDestroy {
  @ViewChild('predefinedFilter') predefinedFilterRef: ElementRef<HTMLInputElement>;
  dataSource: SlicedDataSource<JobListItem>;
  form: FilterableFormState<JobListFilter>;
  predefinedFilters: predefinedFilter[];
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  filter: any;
  datesList: EnumDictionary<DatesFilter>;
  total$: Observable<any>;
  _total  = new BehaviorSubject<JobElementTotal[]>([{type: JobElementType.Total, id: 'total-count'}]);
  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: JobListBusinessService,
              private notifications: NotificationService,
              private router: AppRouter,
              private translations: TranslationService,
              private jobNavigation: JobNavigationService,
              private navigationService: BuilderNavigationService,
              private dialogs: DialogService,
              private gridService: GridSettingsService,
              private filterService: FilterRestService,
              private waitingContext: WaitingContext,
              private notificationService: NotificationService,
              public gridDefinitionFactory: GridDefinitionFactory) {
    this.total$ = this._total.asObservable();
    this.gridService.setCurrentTable(TABLE_HAS_PRESETS.jobs);
  }

  get canCreateQuickJob(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.Jobs.CREATE_QUICK_JOB);
  }

  get isResetVisible(): boolean {
    return this.form.getControl('dateType').value
      || this.form.getControl('dateFrom').value
      || this.form.getControl('dateTo').value;
  }

  get isCorpOrMaster(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master);
  }

  get havePermissions(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.VIEW);
  }

  isJobDpp(job: JobListItem): boolean {
    return job.isDpp;
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout, datesFilter, jobFilters } = getRouteInitData(this.route);
    const jobFiltersIds = ['PERSONAL', 'ALL', 'SIGNED', 'APPROVED', 'SLAB', 'NO_SLAB_DOWN', 'SLAB_NOT_PROCEED'];
    this.predefinedFilters = jobFilters.seq().filter((item: any) => jobFiltersIds.includes(item.id));
    this.datesList = datesFilter;
    let isInit = true;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        status: {
          cellClass: ({data}: { data: any }) => ({
            [JobStatus.Dpp]: 'cell--important',
            [JobStatus.NotProceeding]: 'cell--negative',
            [JobStatus.Active]: 'cell--positive',
          }[data.statusValue]),
        },
        contractAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        variationsAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        totalAmount: buildNumberColumnLeft(NumberTypes.Price),
        totalAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        estimateCost: buildNumberColumnLeft(NumberTypes.Price),
        estimatedProfit: buildNumberColumnLeft(NumberTypes.Price),
        profitMargin: buildNumberColumnLeft(NumberTypes.Price),
        originalBudget: buildNumberColumnLeft(NumberTypes.Price),
        originalContractAmount: buildNumberColumnLeft(NumberTypes.Price),
        originalProfitMargin: buildNumberColumnLeft(NumberTypes.Price),
        currentBudget: buildNumberColumnLeft(NumberTypes.Price),
        totalInvoices: buildNumberColumnLeft(NumberTypes.Price),
        drawsMadeWithGst: buildNumberColumnLeft(NumberTypes.Price),
        actualProfit: buildNumberColumnLeft(NumberTypes.Price),

        finalizedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        contractDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        contracturalCompletionDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        saleDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        financeApprovedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        csDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        councilApprovedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        authToCommenceDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        contractStartDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        slabDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        lastAccountingDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        finalClaimDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        handoverDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        notProceedingDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowNodeId: (item: JobListItem) => item.id,
        processRowPostCreate: conditionalRowPostCreateDecorator(
          ({ node: { data } }) => isJobElementTotal(data),
          textOverlayRowPostCreate(this.translations.get('lbl_total').toUpperCase())),
      });
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: initFilter,
      children: {
        searchString: void 0,
        showFinalized: void 0,
        showDpp: void 0,
        showActive: void 0,
        showNotProceeding: void 0,
        showUserAssignedJobs: void 0,
        isSlabExist: void 0,
        dateType: void 0,
        dateFrom: void 0,
        dateTo: void 0,
        filterType: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.filter = filter;
      const dateTo = new Date(filter.dateTo || '');
      const dateFrom = new Date(filter.dateFrom || '');
      const fixedFilter = {
        ...filter,
        dateFrom: filter.dateFrom ? `${[dateFrom.getFullYear(), dateFrom.getMonth() + 1, dateFrom.getDate()].map(this.stringifyDateComponent).join('-')}` : undefined,
        dateTo: filter.dateTo ? `${[dateTo.getFullYear(), dateTo.getMonth() + 1, dateTo.getDate()].map(this.stringifyDateComponent).join('-')}` : undefined,
      };
      this.dataSource = this.businessService.findAllAsDataSource({
        ...fixedFilter,
      });
      if (!isInit) {
        this.filterService.update(FilterNames.jobs, fixedFilter).subscribe();
      }
      isInit = false;
    });
  }

  ngOnDestroy(): void {
    this.gridService.leaveTable();
  }

  onQuickJobCreate(): void {
    this.router.navigateToChild(['/job-admin/jobs/quick/new']);
  }

  onJobDetailsOpen(job: JobListItem): void {
    if (!this.isElement(job)) {
      return;
    }
    this.jobNavigation.onJobDetailsOpen(job, 'lbl_jobs');
  }

  goToAel(job?: JobListItem): void {
    this.navigationService.goToAel({
      label: 'lbl_jobs',
      entityType: 'JOB',
      entityId: job ? job.id : undefined,
    });
  }

  validateDate(): void {
    const dateFrom = this.form.getControl('dateFrom');
    const dateTo = this.form.getControl('dateTo');
    try {
      if (!(new Date(dateFrom.value).getDate())) {
        dateFrom.reset('');
      }
    } catch (e) {
      dateFrom.reset('');
    }
    try {
      if (!(new Date(dateTo.value).getDate())) {
        dateTo.reset('');
      }
    } catch (e) {
      dateTo.reset('');
    }
  }

  onSearch(): void {
    this.validateDate();
    this.predefinedFilterRef['value'] = '';
    this.form.emitFilter();
  }

  onJobSpecificationOpen(job: JobListItem): void {
    this.jobNavigation.onJobSpecificationOpen(job, 'lbl_jobs');
  }

  onColorSelectionOpen(job: JobListItem): void {
    this.jobNavigation.onColorSelectionOpen(job, 'lbl_jobs');
  }

  onJobEstimateOpen(job: JobListItem): void {
    this.jobNavigation.onJobEstimateOpen(job);
  }

  onFinancialsOpen(job: JobListItem): void {
    this.jobNavigation.onFinancialsOpen(job);
  }

  onJobScheduleOpen(): void {
    // not implemented yet
  }

  onJobOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobOrderOpen(job, 'lbl_jobs');
  }

  onJobChangeOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobChangeOrderOpen(job, 'lbl_jobs');
  }

  validateGDrive(job: JobListItem): void {
    this.businessService.validate(job.id).subscribe(() => {
      this.notificationService.success('msg_validate_job_google_folders_task_added', {jobNumber: job.jobNumber});
    },                                              (error) => {
      this.notificationService.processAll(error);
    });
  }

  onLoadData(event: any): void {
    const totals = event.totals;
    const total = {};
    for (const key in totals) {
      if (totals.hasOwnProperty(key)) {
        total[key] = totals[key];
      }
    }
    this._total.next([{type: JobElementType.Total, id: 'total-count', variationId: 'variation:footer', ...total  }]);
  }

  isElement(element: any): boolean {
    return element.type !== JobElementType.Total;
  }

  onExport(): void {
    const columns = this.gridDefinition.layout.columns.map((column) =>  ({
      width: column.width,
      column: column.column,
      visible: column.visible,
      position: column.position,
      sortable: column.sortable,
      resizable: column.resizable,
    })).filter((column) => column.visible);
    const process = this.waitingContext.start(WaitingMode.Background);
    this.businessService.export(this.filter, columns).subscribe(
     ({url}) => {
      this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
        title: this.translations.get('lbl_link_to_google_sheet'),
        html: `${this.translations.get('lbl_link_to_generated_doc')}:\n${createLink(url)}`,
        buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
      }).afterClosed();
      process.stop();
       // tslint:disable-next-line:align
    }, (error) => {
       this.notificationService.processAll(error);
       process.stop();
    });
  }

  onRecalculate(): void {
    this.businessService.recalculate().subscribe(() => {
      this.notificationService.success('msg_recalculate_jobs_success');
    },                                           (error) => {
      this.notificationService.processAll(error);
    });
  }

  stringifyDateComponent(n: number): string {
    return padStart(toString(n), 2, '0'); // tslint:disable-line:no-magic-numbers
  }
  onResetPredefined(): void {
    this.form.getControl('dateType').reset();
    this.form.getControl('dateFrom').reset('');
    this.form.getControl('dateTo').reset('');
    this.form.getControl('filterType').reset('');
    this.onSearch();
  }
  onReset(): void {
    this.form.getControl('dateType').reset();
    this.form.getControl('dateFrom').reset('');
    this.form.getControl('dateTo').reset('');
    this.form.emitFilter();
  }

  onResetAll(): void {
    this.form.getControl('isSlabExist').setValue('');
    this.form.getControl('dateType').reset();
    this.form.getControl('dateFrom').reset('');
    this.form.getControl('dateTo').reset('');
    this.form.getControl('showActive').setValue(false);
    this.form.getControl('showDpp').setValue(false);
    this.form.getControl('showNotProceeding').setValue(false);
    this.form.getControl('showFinalized').setValue(false);
  }

  onSelectPredefinedFilters(filterName: string): void {
    this.onResetAll();
    switch (filterName)  {
      case 'ALL':
        this.form.getControl('showDpp').setValue(true);
        this.form.getControl('showActive').setValue(true);
        this.form.getControl('showNotProceeding').setValue(true);
        break;
      case 'APPROVED':
        this.form.getControl('showActive').setValue(true);
        this.form.getControl('dateType').setValue(DatesFilter.PermitApproval);
        break;
      case 'NO_SLAB_DOWN':
        this.form.getControl('showActive').setValue(true);
        this.form.getControl('isSlabExist').setValue(false);
        break;
      case 'SIGNED':
        this.form.getControl('showActive').setValue(true);
        this.form.getControl('dateType').setValue(DatesFilter.ContractSigned);
        break;
      case 'SLAB':
        this.form.getControl('showActive').setValue(true);
        this.form.getControl('dateType').setValue(DatesFilter.Slab);
        break;
      case 'SLAB_NOT_PROCEED':
        this.form.getControl('showActive').setValue(true);
        this.form.getControl('dateType').setValue(DatesFilter.Slab);
        this.form.getControl('showNotProceeding').setValue(false);
        break;
      default:
        this.form.getControl('showDpp').setValue(true);
        this.form.getControl('showActive').setValue(true);
        break;
    }
    this.form.emitFilter();
  }
}
